import {ReactNode} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {Center, Loader} from '@mantine/core';
import {useAuth} from '../hooks/useAuth';

// Route guard for pages that need a signed-in user.
//
// ═══ WHY IT READS `status` AND NOT `user` ═══
//
// ⚠️ `user` CANNOT ANSWER THIS QUESTION. It is undefined before Amplify has
// resolved the session AND undefined when the session resolves to "nobody is
// signed in". A guard branching on `user` alone therefore redirects on the
// very first render, before the answer exists — so a signed-in user is bounced
// to the landing page on every hard refresh, and the bug looks intermittent
// because it depends on whether the session resolved before paint.
//
// The platform has filed this twice: FNP-1114 against flexi-email-app and
// FNP-1416 against flexinet-console-app. Both had a guard shaped like
// `if (!user) return <Navigate to="/"/>`. Do not reintroduce that shape here —
// this is the skeleton, so the defect would be copied into every application
// provisioned from it.
//
// ⚠️ The three states are genuinely distinct and all three need handling:
//   resolving     — render nothing conclusive; wait
//   anonymous     — redirect, and remember where they were going
//   authenticated — render the page
export const RequireAuth = ({children}: {children: ReactNode}) => {
    const {status} = useAuth();
    const location = useLocation();

    if (status === 'resolving') {
        // A spinner rather than null: returning null here makes the page flash
        // blank on a slow session fetch, which reads as a broken app.
        return (
            <Center h="100vh">
                <Loader/>
            </Center>
        );
    }

    if (status === 'anonymous') {
        // ⚠️ `state.from` is what lets the sign-in flow return the user to the
        // page they asked for. Dropping it sends every authenticated user to
        // the landing page instead, which is a silent downgrade rather than a
        // visible error — the reason it survives unnoticed in apps that omit it.
        return <Navigate to="/" replace state={{from: location}}/>;
    }

    return <>{children}</>;
};
